import React, { useCallback, useEffect, useMemo } from "react";
import { colors } from '../../../theme/colors';
import {
  Box, Button, Typography, IconButton, Tooltip, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import Editor from "@monaco-editor/react";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { CodeEditorWrapper, BottomBarWrapper } from "./style";

const LANGUAGE_MAP = {
  python: "python",
  javascript: "javascript",
  java: "java",
  c: "c",
  cpp: "cpp",
  "c++": "cpp",
  sql: "sql",
  typescript: "typescript",
  go: "go",
  ruby: "ruby",
  rust: "rust",
};

const LANGUAGE_LABELS = {
  python: "Python",
  javascript: "JavaScript",
  java: "Java",
  c: "C",
  cpp: "C++",
  "c++": "C++",
  sql: "SQL",
  typescript: "TypeScript",
  go: "Go",
  ruby: "Ruby",
  rust: "Rust",
};

const CodeEditorSection = ({
  code,
  language,
  onChange,
  onSave,
  onPrevious,
  onNext,
  onSubmit,
  isFirstQuestion,
  isLastQuestion,
  isSaved,
}) => {
  const [fontSize, setFontSize] = React.useState(14);
  const editorRef = React.useRef(null);

  const languages = useMemo(() => {
    if (Array.isArray(language)) return language;
    if (typeof language === "string" && language.trim()) return [language];
    return [];
  }, [language]);

  const [selectedLanguage, setSelectedLanguage] = React.useState(
    languages[0] || ""
  );
  const [pendingLanguage, setPendingLanguage] = React.useState(null);
  const [showSwitchDialog, setShowSwitchDialog] = React.useState(false);

  useEffect(() => {
    setSelectedLanguage(languages[0] || "");
  }, [languages]);

  const handleLanguageChange = useCallback((e) => {
    const newLang = e.target.value;
    if (newLang === selectedLanguage?.toLowerCase()) return;
    setPendingLanguage(newLang);
    setShowSwitchDialog(true);
  }, [selectedLanguage]);

  const handleConfirmSwitch = useCallback(() => {
    setSelectedLanguage(pendingLanguage);
    onChange("");
    setShowSwitchDialog(false);
    setPendingLanguage(null);
  }, [pendingLanguage, onChange]);

  const handleCancelSwitch = useCallback(() => {
    setShowSwitchDialog(false);
    setPendingLanguage(null);
  }, []);

  const showDropdown = languages.length > 1;
  const monacoLang = LANGUAGE_MAP[selectedLanguage?.toLowerCase()] || "plaintext";

  const handleEditorMount = useCallback((editor) => {
    editorRef.current = editor;

    editor.onKeyDown((e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        [
          "KeyC", "KeyV", "KeyX", "KeyS", "KeyA",
        ].includes(e.code)
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
  }, []);

  const handleFormat = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.getAction("editor.action.formatDocument")?.run();
    }
  }, []);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <CodeEditorWrapper>
        <Box className="editor-toolbar">
          {showDropdown ? (
            <Select
              value={selectedLanguage?.toLowerCase()}
              onChange={handleLanguageChange}
              size="small"
              sx={{
                height: "30px",
                fontSize: "12px",
                fontWeight: 600,
                color: "#ccc",
                backgroundColor: "#3e3e3e",
                borderRadius: "6px",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #555",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#888",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.primary,
                },
                "& .MuiSelect-icon": { color: "#999" },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: "#2d2d2d",
                    border: "1px solid #555",
                    "& .MuiMenuItem-root": {
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#ccc",
                      "&:hover": { backgroundColor: "#3e3e3e" },
                      "&.Mui-selected": {
                        backgroundColor: colors.primary,
                        color: "#fff",
                        "&:hover": { backgroundColor: colors.primaryHover },
                      },
                    },
                  },
                },
              }}
            >
              {languages.map((lang) => (
                <MenuItem key={lang} value={lang.toLowerCase()}>
                  {LANGUAGE_LABELS[lang.toLowerCase()] || lang}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <Typography className="editor-lang-badge">{monacoLang}</Typography>
          )}
          <Box className="editor-actions">
            <Tooltip title="Zoom Out">
              <IconButton
                size="small"
                onClick={() => setFontSize((s) => Math.max(10, s - 1))}
                sx={{ color: "#999" }}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom In">
              <IconButton
                size="small"
                onClick={() => setFontSize((s) => Math.min(24, s + 1))}
                sx={{ color: "#999" }}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Format Code">
              <IconButton
                size="small"
                onClick={handleFormat}
                sx={{ color: "#999" }}
              >
                <FormatAlignLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box className="editor-container">
          <Editor
            height="100%"
            language={monacoLang}
            value={code}
            onChange={onChange}
            onMount={handleEditorMount}
            theme="vs-dark"
            options={{
              fontSize,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              lineNumbers: "on",
              wordWrap: "on",
              automaticLayout: true,
              contextmenu: false,
              tabSize: 2,
              padding: { top: 12 },
              renderLineHighlight: "line",
              smoothScrolling: true,
              cursorBlinking: "smooth",
              cursorSmoothCaretAnimation: "on",
            }}
          />
        </Box>
      </CodeEditorWrapper>

      <BottomBarWrapper>
        <Button
          variant="outlined"
          disabled={isFirstQuestion}
          onClick={onPrevious}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            borderColor: "#d0d5dd",
            color: "#344054",
            fontWeight: 600,
            "&:hover": { borderColor: colors.primary, color: colors.primary },
          }}
        >
          Previous
        </Button>

        <Button
          variant="outlined"
          onClick={onSave}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            borderColor: isSaved ? "#43a047" : "#d0d5dd",
            color: isSaved ? "#2e7d32" : "#344054",
            fontWeight: 600,
            "&:hover": { borderColor: colors.primary, color: colors.primary },
          }}
        >
          {isSaved ? "Saved" : "Save"}
        </Button>

        {isLastQuestion ? (
          <Button
            variant="contained"
            onClick={onSubmit}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              backgroundColor: colors.primary,
              fontWeight: 600,
              px: 4,
              "&:hover": { backgroundColor: colors.primaryHover },
            }}
          >
            Submit Assessment
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={onNext}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              backgroundColor: colors.primary,
              fontWeight: 600,
              px: 4,
              "&:hover": { backgroundColor: colors.primaryHover },
            }}
          >
            Next Question
          </Button>
        )}
      </BottomBarWrapper>

      <Dialog
        open={showSwitchDialog}
        onClose={handleCancelSwitch}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            maxWidth: "420px",
            width: "100%",
            padding: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            fontWeight: 700,
            fontSize: "18px",
            color: "#1d2939",
          }}
        >
          <WarningAmberRoundedIcon sx={{ color: "#f59e0b" }} />
          Switch Language
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#344054", fontSize: "14px", lineHeight: 1.6 }}>
            Switching languages will reset your current code editor content. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "8px 24px 16px", gap: "8px" }}>
          <Button
            onClick={handleCancelSwitch}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              borderColor: "#d0d5dd",
              color: "#344054",
              fontWeight: 600,
              "&:hover": { borderColor: colors.primary, color: colors.primary },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSwitch}
            variant="contained"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              backgroundColor: colors.primary,
              fontWeight: 600,
              "&:hover": { backgroundColor: colors.primaryHover },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CodeEditorSection;
